import { Grid } from '@mui/material';
import {
  Button,
  ConfirmationModalContent,
  DatePicker,
  Dropdown,
  ErrorContainer,
  InputField,
  LabelComponent,
  ModalPopup,
  Stack
} from 'components';
import AccordionComponent from 'components/accordion/accordion';
import { FieldArray, getIn, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useState } from 'react';
import { useAppSelector } from 'store/hooks';
import { useResponsive } from 'utils/hooks/useMediaQuery';

import { FormValues } from '../../clientForm';
import { isSectionInvalid } from '../personalDetails/personalDetails';
import style from '../primaryAddress/primaryAddress.module.scss';
import SectionTitle from '../sectionTitle/sectionTitle';
import demographicsStyle from './demographics.module.scss';

const genders = [
  { id: 'Woman', name: 'Woman' },
  { id: 'Man', name: 'Man' },
  {
    id: 'Non-binary/Genderqueer/Gender nonconforming',
    name: 'Non-binary/Genderqueer/Gender nonconforming'
  },
  { id: 'Transgender', name: 'Transgender' },
  { id: 'Self-described', name: 'Self-described' }
];
const veteranStatus = [
  { id: 'true', name: ' Yes -- has been discharged from the armed forces of the United States' },
  { id: 'false', name: 'No' }
];
const racesList = [
  {
    id: 'AMERICAN_INDIAN_OR_ALASKA_NATIVE',
    name: 'American Indian Or Alaska Native'
  },
  {
    id: 'ASIAN',
    name: 'Asian'
  },
  {
    id: 'BLACK_OR_AFRICAN_AMERICAN',
    name: 'Black Or African American'
  },
  {
    id: 'HISPANIC_OR_LATINO',
    name: 'Hispanic Or Latino'
  },
  {
    id: 'MIDDLE_EASTERN_OR_NORTH_AFRICAN',
    name: 'Middle Eastern Or North African'
  },
  {
    id: 'NATIVE_HAWAIIAN_OR_OTHER_PACIFIC_ISLANDER',
    name: 'Native Hawaiian Or Other Pacific Islander'
  },
  {
    id: 'WHITE',
    name: 'White'
  },
  {
    id: 'SELF_DESCRIBED',
    name: 'Self Described'
  }
];
function booleanToString(value: any): any {
  // debugger;
  if (value === null) return '';

  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';

  return null;
}

const Demographics = () => {
  const { isTablet } = useResponsive();
  const formik = useFormikContext<FormValues>();
  const [deleteModalOpen, setDeleteModalOpen] = useState<number | boolean>(false);

  const insurancesList = useAppSelector((state) => state.clients.insurancesAutocomplete.data);
  const isSelfDescribed = formik.values.races?.filter((race) => race?.id === 'SELF_DESCRIBED')[0];
  const selfDescribed = formik?.values?.races?.find((race) => race?.id === 'SELF_DESCRIBED');

  const { phpMaximonInsuranceUpdates } = useFlags();

  return (
    <AccordionComponent
      accordionTabId="fifth"
      title="Demographics"
      showWarning={
        isSectionInvalid(formik?.errors, [
          'language',
          'veteranStatus',
          'genderIdentity',
          'races',
          'selfDescribed',
          'genderSelfDescribed'
        ]) ||
        isSectionInvalid(
          formik.errors?.insurances,
          [
            'insuranceCategoryId',
            'insuranceCategoryName',
            'insurancePlan',
            'insuranceNumber',
            'startDate',
            'endDate'
          ],
          true
        )
      }
    >
      <Stack gap="24px" data-test-id="profile-insurance" marginTop={'24px'}>
        <SectionTitle>Insurance</SectionTitle>
        <>
          <FieldArray
            name="insurances"
            render={(arrayHelpers) => (
              <div>
                {formik.values.insurances.map((emergencyContact, index) => (
                  <>
                    <Grid
                      container
                      columnSpacing={'16px'}
                      key={index}
                      mb={'9px'}
                      mt={index !== 0 ? '24px' : '0'}
                    >
                      <Grid xs={isTablet ? 12 : 3.6} item>
                        <LabelComponent
                          text={`Insurance Category ${index !== 0 ? index + 1 : ''}`}
                        />
                        <Dropdown
                          value={formik.values.insurances[index].insuranceCategoryId ?? ''}
                          name={`insurances[${index}].insuranceCategoryId`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          label="Select"
                          data={insurancesList ?? []}
                        />
                        <ErrorContainer
                          visible={
                            formik.touched?.insurances?.[index]?.insuranceCategoryId &&
                            getIn(formik.errors, `insurances[${index}].insuranceCategoryId`)
                          }
                          errors={formik.errors?.insurances?.[index]}
                          name="insuranceCategoryId"
                        />
                      </Grid>
                      <Grid xs={isTablet ? 12 : 3.6} item>
                        <LabelComponent text={`Insurance Plan ${index !== 0 ? index + 1 : ''}`} />
                        <InputField
                          value={formik.values.insurances[index].insurancePlan}
                          name={`insurances[${index}].insurancePlan`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Enter"
                        />
                        <ErrorContainer
                          visible={
                            formik.touched?.insurances?.[index]?.insurancePlan &&
                            getIn(formik.errors, `insurances[${index}].insurancePlan`)
                          }
                          errors={formik.errors?.insurances?.[index]}
                          name="insurancePlan"
                        />
                      </Grid>
                      <Grid xs={isTablet ? 12 : 3.6} item>
                        <LabelComponent text={`Insurance Number ${index !== 0 ? index + 1 : ''}`} />
                        <InputField
                          value={formik.values.insurances[index].insuranceNumber}
                          name={`insurances[${index}].insuranceNumber`}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          placeholder="Enter"
                        />
                        <ErrorContainer
                          visible={
                            formik.touched?.insurances?.[index]?.insuranceNumber &&
                            getIn(formik.errors, `insurances[${index}].insuranceNumber`)
                          }
                          errors={formik.errors?.insurances?.[index]}
                          name="insuranceNumber"
                        />
                      </Grid>
                      {!isTablet && (
                        <Grid
                          xs={isTablet ? 12 : 1.2}
                          pt={'24px'}
                          display={'flex'}
                          justifyContent={'end'}
                        >
                          <span
                            className={'material-icons'}
                            style={{
                              marginTop: '4px',
                              color: '#096F84',
                              padding: '8px',
                              cursor: 'pointer',
                              visibility: index !== 0 ? 'visible' : 'hidden'
                            }}
                            onClick={() => setDeleteModalOpen(index)}
                          >
                            delete
                          </span>
                        </Grid>
                      )}
                      {phpMaximonInsuranceUpdates && (
                        <>
                          <Grid xs={isTablet ? 12 : 3.6} item>
                            <LabelComponent text={`Start Date ${index !== 0 ? index + 1 : ''}`} />
                            <DatePicker
                              value={formik.values.insurances[index].startDate}
                              onChange={(value: string) => {
                                formik
                                  .setFieldValue(`insurances[${index}].startDate`, value)
                                  // @ts-expect-error this is valid but the formik types are incorrect
                                  .then(() => {
                                    formik.setFieldTouched(`insurances[${index}].startDate`, true);
                                  });
                              }}
                              onBlur={() =>
                                formik.setFieldTouched(`insurances[${index}].startDate`, true)
                              }
                              inputPlaceholder="Enter"
                            />
                            <ErrorContainer
                              visible={
                                formik.touched?.insurances?.[index]?.startDate &&
                                getIn(formik.errors, `insurances[${index}].startDate`)
                              }
                              errors={formik.errors?.insurances?.[index]}
                              name="startDate"
                            />
                          </Grid>
                          <Grid xs={isTablet ? 12 : 3.6} item>
                            <LabelComponent text={`End Date ${index !== 0 ? index + 1 : ''}`} />
                            <DatePicker
                              value={formik.values.insurances[index].endDate}
                              onChange={(value: string) => {
                                formik
                                  .setFieldValue(`insurances[${index}].endDate`, value)
                                  // @ts-expect-error this is valid but the formik types are incorrect
                                  .then(() => {
                                    formik.setFieldTouched(`insurances[${index}].endDate`, true);
                                  });
                              }}
                              onBlur={() =>
                                formik.setFieldTouched(`insurances[${index}].endDate`, true)
                              }
                              inputPlaceholder="Enter"
                            />
                            <ErrorContainer
                              visible={
                                formik.touched?.insurances?.[index]?.endDate &&
                                getIn(formik.errors, `insurances[${index}].endDate`)
                              }
                              errors={formik.errors?.insurances?.[index]}
                              name="endDate"
                            />
                          </Grid>
                        </>
                      )}
                      {isTablet && index !== 0 && (
                        <Grid
                          xs={isTablet ? 12 : 1.2}
                          pt={'24px'}
                          display={'flex'}
                          justifyContent={'end'}
                        >
                          <span
                            className={'material-icons'}
                            style={{
                              marginTop: '4px',
                              color: '#096F84',
                              padding: '8px',
                              cursor: 'pointer'
                            }}
                            onClick={() => setDeleteModalOpen(index)}
                          >
                            delete
                          </span>
                        </Grid>
                      )}
                    </Grid>
                    {index !== formik.values.insurances.length - 1 && (
                      <div className={demographicsStyle.line} />
                    )}
                  </>
                ))}
                {formik.values.insurances && formik.values.insurances.length < 3 && (
                  <Stack width="160px">
                    <Button
                      icon={<span className="material-icons">add</span>}
                      theme="transparent"
                      type="button"
                      text="Add another"
                      handleOnClick={() =>
                        arrayHelpers.push({
                          insuranceCategoryId: '',
                          insuranceCategoryName: '',
                          insurancePlan: '',
                          insuranceNumber: ''
                        })
                      }
                    />
                  </Stack>
                )}
                <ModalPopup
                  open={Boolean(deleteModalOpen)}
                  customClassInsideContainer={style.deleteModalDemographics}
                  handleClose={() => setDeleteModalOpen(false)}
                >
                  <ConfirmationModalContent
                    confirmButtonText="Yes, delete"
                    rejectButtonText="No, return to editing"
                    title="Are you sure you want to delete this row?"
                    text=" "
                    handleConfirm={() => {
                      arrayHelpers.remove(Number(deleteModalOpen));
                      setDeleteModalOpen(false);
                    }}
                    handleReject={() => setDeleteModalOpen(false)}
                  />
                </ModalPopup>
              </div>
            )}
          />
        </>
      </Stack>
      <Stack gap="24px" data-test-id="profile-other-demographics">
        <SectionTitle>Other Demographics</SectionTitle>
        <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }} mt={'12px'}>
          <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
            <Stack width="100%">
              <LabelComponent text="Primary language" />
              <InputField
                value={formik.values.language}
                name="language"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                placeholder="Enter"
              />
              <ErrorContainer
                visible={formik.touched.language && formik.errors.language}
                errors={formik.errors}
                name="language"
              />
            </Stack>
          </Grid>
          <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
            <Stack width="100%">
              <LabelComponent text="Veteran status" />
              <Dropdown
                value={booleanToString(formik.values.veteranStatus)}
                name="veteranStatus"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Select"
                data={veteranStatus}
              />
              <ErrorContainer
                visible={formik.touched.veteranStatus && formik.errors.veteranStatus}
                errors={formik.errors}
                name="veteranStatus"
              />
            </Stack>
          </Grid>
          <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
            <Stack width="100%">
              <LabelComponent text="Gender Identity" />
              <Dropdown
                value={formik.values.genderIdentity ?? ''}
                name="genderIdentity"
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                label="Select"
                data={genders}
              />
              <ErrorContainer
                visible={formik.touched.genderIdentity && formik.errors.genderIdentity}
                errors={formik.errors}
                name="genderIdentity"
              />
            </Stack>
            {formik.values.genderIdentity === 'Self-described' && (
              <Stack mt={'24px'}>
                <LabelComponent text="Self-described Gender Identity" />
                <InputField
                  name="genderSelfDescribed"
                  onBlur={formik.handleBlur}
                  onChange={formik.handleChange}
                  value={formik.values.genderSelfDescribed}
                  placeholder="Type description"
                />
                <ErrorContainer
                  visible={formik.touched.genderSelfDescribed && formik.errors.genderSelfDescribed}
                  errors={formik.errors}
                  name="genderSelfDescribed"
                />
              </Stack>
            )}
          </Grid>
          <Grid item xs={isTablet ? 12 : 6} mb={isTablet ? '0px' : '16px'}>
            <Stack width="100%">
              <LabelComponent text="Race/Ethnicity" />
              <Dropdown
                value={formik.values.races?.map((race) => race?.id) ?? []}
                data={racesList}
                onChange={(e) => {
                  let selectedRaces: any = e.target.value;
                  selectedRaces = selectedRaces?.map((x: any) => {
                    return { id: x, name: x === 'SELF_DESCRIBED' ? '' : x };
                  });
                  formik.setFieldValue('races', [...selectedRaces]);
                }}
                label="Select"
                multiSelect
              />
              <ErrorContainer
                visible={formik.touched.races && formik.errors.races}
                errors={formik.errors}
                name="races"
              />
            </Stack>
            {!!isSelfDescribed && (
              <Stack width="100%">
                <LabelComponent text="Self-described Race/Ethnicity" />
                <InputField
                  value={formik.values.selfDescribed ?? selfDescribed?.name}
                  name="selfDescribed"
                  onChange={(e) => {
                    const updatedRaces = formik?.values?.races?.map((race) =>
                      race.id === 'SELF_DESCRIBED' ? { ...race, name: e.target.value } : race
                    );

                    // Update the formik values with the modified array
                    formik.setFieldValue('races', updatedRaces);
                    formik.handleChange(e);
                  }}
                  onBlur={formik.handleBlur}
                  placeholder="Type description"
                />
                <ErrorContainer
                  visible={formik.touched.selfDescribed && formik.errors.selfDescribed}
                  errors={formik.errors}
                  name="selfDescribed"
                />
              </Stack>
            )}
          </Grid>
        </Grid>
      </Stack>
    </AccordionComponent>
  );
};

export default Demographics;
